import React, { useState, useEffect } from 'react';
import { array, func } from 'prop-types';
import { useIntl } from 'react-intl';
import { useUserContext } from '../../../../aem-core-components/context/UserContext';
import { useFilterState } from '../../../cap';
import { useCartState } from '../../../../contexts/cart';
import { useAnalyticsContext } from '../../../../config/GoogleTagManagerEvents';
import useCheckLocationEmpty from '../../../../hooks/useCheckLocationEmpty';
import { useCheckUser } from '../../../../hooks/useCheckUser';
import useProductTile from '../../../../hooks/useProductTile';
import useAnalytics from '../../../../hooks/analytics/useAnalytics';
import useCartAnalytics from '../../../../hooks/analytics/useCartAnalytics';
import { useCartOperations } from '../../hooks/useCart';
import { logError } from '../../../global/utils/logger';
import { currencyToLocale } from '../../../../components/global/utils/commonUtils';
import LazyLoadComponent from '../../../global/lazyLoad/LazyLoadComponent';
import useOrderSummaryFactory from '../../../molecules/orderSummaryFactory/hooks/useOrderSummaryFactory';
import SelectRentalLocationDates from '../../../global/modules/productTile/atoms/selectRentalLocationDates/SelectRentalLocationDates';
import AllItemsUnavailableModal from '../allItemsUnavailableModal/AllItemsUnavailableModal';
import FooterShimmer from '../../cartShimmer/FooterShimmer';
import { LoadingIndicator } from '../../../../aem-core-components';
import Button from '../../../../components/global/atoms/button';
import Footer from './Footer';
import { cartDataLocators } from './dataLocators';
import { RESET_ORDER_ESTIMATES } from '../../../../aem-core-components/actions/constants';
import { EMPLOYEE } from '../../../global/constants';
import { VIEW_CART } from '../../../../constants/cartConstants';
import { STORAGE_CONFIG } from '../../../../constants/storageConfig';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../constants/analyticsConstants/Ecommerce';
import { USER_TYPE } from '../../../../constants/userDetailsConstants';
import { FACTORYSOURCE } from '../../../molecules/orderSummaryFactory/constants/orderSummaryFactoryConstants';

const CartFooter = props => {
    const { handleCheckoutBtnClick, availableItemArr, unavailableItemsArr, conditionallyAvailableArr } = props;
    const cartIntl = useIntl();
    const [userState] = useUserContext();
    const { userProfile } = userState;
    const { sendEventsForEcommerceAction } = useAnalyticsContext();
    const [{ payloadEcommerceActionAnalytics, payloadEcommerceLocationActionAnalytics }] = useAnalytics();
    const [{ triggerCartViewedEvent }] = useCartAnalytics();
    const { openEmptyLocationDateModal } = useProductTile();
    const [
        { cart, isCreditNewAddress, locationPCLoading, isCartLoading, error, isSourcesLoading, loaders },
        dispatch
    ] = useCartState();
    const { isEstimatesLoading } = loaders || {};
    const [{ viewCart, startDate, endDate }] = useFilterState();
    const [{ isProfileLoaded }] = useUserContext();
    const userType = useCheckUser();
    const { isRentalDetailsAvailable } = useCheckLocationEmpty();
    const [isCheckoutEnable, setIsCheckoutEnable] = useState(false);
    const { getSubTotal, orderSummaryDetails } = useOrderSummaryFactory({ source: FACTORYSOURCE.CART });
    const { renderEmptyRentalLabels, handleChange, showCheckoutButton } = useCartOperations({});
    const isCheckoutBtnVisible = showCheckoutButton();
    const orderEstimates = cart?.estimatesResponse?.estimate;
    const { deliveryPickUpCharges, salesTax } = orderEstimates?.totals || {};
    const { showDeliveryEstimate } = viewCart;

    useEffect(() => {
        if (!isCartLoading) {
            try {
                /* Trigger event when no cart is there or with updated cart on mincart open */
                sendEventsForEcommerceAction(
                    EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CHECKOUT_VIEWED,
                    payloadEcommerceActionAnalytics(),
                    {
                        unavailable_items: unavailableItemsArr?.length > 0
                    }
                );
            } catch (error) {
                logError(error, false, 'sendEventsForEcommerceAction');
            }
        }
    }, [isCartLoading]);

    useEffect(() => {
        if (userType === USER_TYPE.CREDIT && isCreditNewAddress) {
            handleChange(VIEW_CART.CREATE_PROJECT_SOURCE, 'cart');
        }
    }, [isCreditNewAddress]);

    useEffect(() => {
        if (!locationPCLoading && isProfileLoaded) {
            try {
                const userData = payloadEcommerceLocationActionAnalytics();
                sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.USER_DATA_OBJ, JSON.stringify(userData));
            } catch (error) {
                logError(error, false, 'payloadEcommerceLocationActionAnalytics');
            }
        }
    }, [locationPCLoading, isProfileLoaded]);

    useEffect(() => {
        if (orderSummaryDetails?.rentalSubtotal && !availableItemArr?.length) {
            dispatch({
                type: RESET_ORDER_ESTIMATES
            });
        }
    }, [orderSummaryDetails?.rentalSubtotal, availableItemArr]);

    useEffect(() => {
        if (isCheckoutEnable) {
            const additionalPayload = {
                form_name: EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_ALL_ITEMS_UNAVAILABLE,
                form_type: EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_INFORMATION
            }
            triggerCartViewedEvent(EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_VIEWED, additionalPayload, availableItemArr, conditionallyAvailableArr, unavailableItemsArr);
        }
    }, [isCheckoutEnable]);

    const toggleIsAllItemsUnavailableModalOpen = () => {
        setIsCheckoutEnable(!isCheckoutEnable);
    };

    const handleCheckoutBtnClickModal = () => {
        try {
            if (!availableItemArr?.length) {
                setIsCheckoutEnable(true);
            } else {
                handleCheckoutBtnClick();
            }
        } catch (error) {
            logError(error, false, 'handleCheckoutBtnClickModal');
        }
    };

    const showCheckoutError = () => {
        if (error?.timeZoneError) {
            return { message: error?.timeZoneError, error: true };
        } else if (error?.startDateError) {
            return { message: error?.startDateError, error: true };
        } else if (error?.endDateError) {
            return { message: error?.endDateError, error: true };
        } else if (error?.timeSlotsError) {
            return { message: error?.timeSlotsError, error: true };
        } else {
            return { message: '', error: false };
        }
    };

    if (isEstimatesLoading) {
        return <FooterShimmer isCheckoutBtnVisible={isCheckoutBtnVisible} />;
    }

    return (
        <LazyLoadComponent fallback={<LoadingIndicator />}>
            {isCheckoutEnable && (
                <AllItemsUnavailableModal isOpen toggleModal={toggleIsAllItemsUnavailableModalOpen} isCheckoutEnable />
            )}
            <Footer>
                <div className={userProfile?.type != EMPLOYEE ? 'cart-footer-top' : 'cart-footer-top-without-checkout'}>
                    <div className="cart-footer-prices">
                        <strong data-testid={cartDataLocators.minicart_estimatedsubtotal_label}>
                            {cartIntl.formatMessage({ id: 'cart-footer:estimated-subtotal' })}
                        </strong>
                        {cart?.items?.length > 0 ? (
                            <div className="cart-footer-total">
                                <span>
                                    <strong data-testid={cartDataLocators.minicart_dynamicprice_label}>
                                        {isRentalDetailsAvailable() ? currencyToLocale(getSubTotal(salesTax)) : '-'}
                                    </strong>
                                </span>
                                <small className="charge">
                                    {showDeliveryEstimate && deliveryPickUpCharges && !startDate && !endDate
                                        ? `+${currencyToLocale(deliveryPickUpCharges)}`
                                        : null}
                                </small>
                            </div>
                        ) : (
                            <div className="cart-footer-section-shimmer shimmer-cart"></div>
                        )}
                    </div>
                    {!isRentalDetailsAvailable() && (
                        <SelectRentalLocationDates
                            emptyDetailsText={renderEmptyRentalLabels()}
                            openEmptyLocationDateModal={openEmptyLocationDateModal}
                        />
                    )}
                </div>
                {showCheckoutError()?.error && <div>{showCheckoutError()?.message}</div>}
                {isCheckoutBtnVisible &&
                    (isSourcesLoading ? (
                        <div className="button button-block shimmer-cart"></div>
                    ) : (
                        <div className="cart-footer-actions">
                            <Button
                                onClick={handleCheckoutBtnClickModal}
                                type="button"
                                className={'button button-primary button-block'}
                                data-testid={cartDataLocators.minicart_checkout_CTA}
                                buttonAriaLabel={`${cartIntl.formatMessage({ id: 'cart-footer:checkout' })}`}>
                                {cartIntl.formatMessage({ id: 'cart-footer:checkout' })}
                            </Button>
                        </div>
                    ))}
            </Footer>
        </LazyLoadComponent>
    );
};

export default React.memo(CartFooter);

CartFooter.propTypes = {
    handleCheckoutBtnClick: func,
    availableItemArr: array,
    unavailableItemsArr: array,
    conditionallyAvailableArr: array
};
CartFooter.defaultProps = {
    handleCheckoutBtnClick: () => { },
    availableItemArr: [],
    unavailableItemsArr: [],
    conditionallyAvailableArr: []
};
